import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, RefreshCw, Download, MessageSquareHeart, UserX, Trash2, MessageSquare, Filter } from 'lucide-react';
import { fetchOffboardingSurveys, OffboardingSurvey } from '../../../services/firebaseService';

type TypeFilter = 'all' | 'cancellation' | 'deletion';
type PeriodFilter = 'all' | '30' | '90' | '365';

// Samme rekkefølge som i OffboardingSurveyModal — labels hentes fra offboarding.* i18n-nøklene.
const REASON_KEYS = ['too_expensive', 'not_using', 'missing_features', 'better_alternative', 'technical_issues', 'just_testing', 'other'] as const;
const RETURN_KEYS = ['lower_price', 'more_spots', 'more_features', 'better_quality', 'nothing', 'other'] as const;

const AdminOffboardingDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState<OffboardingSurvey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOffboardingSurveys();
      setSurveys(data);
    } catch (e: any) {
      console.error('fetchOffboardingSurveys error:', e);
      setError(e?.message || 'Kunne ikke hente tilbakemeldinger.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const cutoff = periodFilter === 'all' ? 0 : Date.now() - parseInt(periodFilter) * 24 * 60 * 60 * 1000;
    return surveys.filter(s => {
      if (typeFilter !== 'all' && s.type !== typeFilter) return false;
      if (cutoff && (s.createdAt == null || s.createdAt < cutoff)) return false;
      return true;
    });
  }, [surveys, typeFilter, periodFilter]);

  const counts = useMemo(() => {
    const cancellation = filtered.filter(s => s.type === 'cancellation').length;
    const deletion = filtered.filter(s => s.type === 'deletion').length;
    const withComment = filtered.filter(s => (s.comment || '').trim().length > 0).length;
    return { total: filtered.length, cancellation, deletion, withComment };
  }, [filtered]);

  // Tell opp en nøkkel-fordeling, sortert etter forhåndsdefinert rekkefølge + ukjente til slutt.
  const tally = (field: 'reason' | 'wouldReturn', keys: readonly string[]) => {
    const map = new Map<string, number>();
    let answered = 0;
    filtered.forEach(s => {
      const v = s[field];
      if (!v) return;
      answered++;
      map.set(v, (map.get(v) || 0) + 1);
    });
    const ordered: Array<{ key: string; count: number }> = [];
    keys.forEach(k => { if (map.has(k)) { ordered.push({ key: k, count: map.get(k)! }); map.delete(k); } });
    Array.from(map.entries()).forEach(([k, count]) => ordered.push({ key: k, count }));
    ordered.sort((a, b) => b.count - a.count);
    return { rows: ordered, answered };
  };

  const reasonTally = useMemo(() => tally('reason', REASON_KEYS), [filtered]);
  const returnTally = useMemo(() => tally('wouldReturn', RETURN_KEYS), [filtered]);

  const segment = (field: 'subscriptionProvider' | 'platform' | 'appLanguage') => {
    const map = new Map<string, number>();
    filtered.forEach(s => {
      const v = (s[field] || '–').toString();
      map.set(v, (map.get(v) || 0) + 1);
    });
    return Array.from(map.entries()).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
  };
  const providerSeg = useMemo(() => segment('subscriptionProvider'), [filtered]);
  const platformSeg = useMemo(() => segment('platform'), [filtered]);
  const langSeg = useMemo(() => segment('appLanguage'), [filtered]);

  const comments = useMemo(
    () => filtered.filter(s => (s.comment || '').trim().length > 0),
    [filtered]
  );

  const reasonLabel = (key: string) => {
    const translated = t(`offboarding.reason.${key}`, '');
    return translated || key;
  };
  const returnLabel = (key: string) => {
    const translated = t(`offboarding.return.${key}`, '');
    return translated || key;
  };
  const fmtDateTime = (ms: number | null) =>
    ms ? new Date(ms).toLocaleString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–';
  const providerLabel = (p: string | null) =>
    p === 'vipps' ? 'Vipps' : p === 'apple' ? 'Apple' : p === 'stripe' ? 'Stripe' : p || '–';

  const downloadCsv = () => {
    const escape = (v: string | number) => {
      const s = String(v ?? '');
      return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows: (string | number)[][] = [
      ['Dato', 'Type', 'Grunn', 'Hva skulle til', 'Kommentar', 'Leverandør', 'Plattform', 'Språk', 'Bruker', 'E-post'],
    ];
    filtered.forEach(s => rows.push([
      s.createdAt ? new Date(s.createdAt).toISOString() : '',
      s.type,
      s.reason ? reasonLabel(s.reason) : '',
      s.wouldReturn ? returnLabel(s.wouldReturn) : '',
      s.comment || '',
      providerLabel(s.subscriptionProvider),
      s.platform || '',
      s.appLanguage || '',
      s.userName || s.userId || '',
      s.userEmail || '',
    ]));
    const csv = '﻿' + rows.map(r => r.map(escape).join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fricamp-tilbakemeldinger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const BarList: React.FC<{
    rows: Array<{ key: string; count: number }>;
    answered: number;
    label: (k: string) => string;
    tone: string;
  }> = ({ rows, answered, label, tone }) => {
    const max = Math.max(...rows.map(r => r.count), 1);
    if (rows.length === 0) {
      return <p className="text-tiny text-[var(--text-muted)] py-4">Ingen svar i utvalget.</p>;
    }
    return (
      <div className="space-y-2.5">
        {rows.map(row => {
          const pct = answered > 0 ? Math.round((row.count / answered) * 100) : 0;
          return (
            <div key={row.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-tiny font-bold text-[var(--text-secondary)] truncate mr-2">{label(row.key)}</span>
                <span className="text-2xs font-black text-[var(--text-primary)] shrink-0">
                  {row.count} <span className="text-[var(--text-muted)] font-bold">· {pct}%</span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-[var(--bg-input)] overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${tone}`} style={{ width: `${(row.count / max) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const SegmentRow: React.FC<{ title: string; rows: Array<{ key: string; count: number }>; render?: (k: string) => string }> = ({ title, rows, render }) => (
    <div className="flex-1 min-w-[140px]">
      <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">{title}</p>
      <div className="space-y-1">
        {rows.map(r => (
          <div key={r.key} className="flex items-center justify-between text-tiny">
            <span className="text-[var(--text-secondary)] truncate mr-2">{render ? render(r.key) : r.key}</span>
            <span className="font-black text-[var(--text-primary)]">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading && surveys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
        <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster tilbakemeldinger...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-500/5 border border-red-500/10 rounded-2xl flex items-start gap-4">
        <AlertCircle className="text-red-400 shrink-0" size={18} />
        <div>
          <p className="text-sm font-bold text-red-400">Data utilgjengelig</p>
          <p className="text-xs text-red-400/70 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Header + filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
            <MessageSquareHeart size={20} className="text-emerald-400" />
          </div>
          <div>
            <p className="text-lg font-black text-[var(--text-primary)]">Oppsigelses-tilbakemeldinger</p>
            <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">
              Svar fra brukere som sier opp abonnement eller sletter konto
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Oppdater
          </button>
          <button onClick={downloadCsv} disabled={filtered.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50">
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[var(--text-muted)]" />
          <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mr-1">Type:</span>
          {(['all', 'cancellation', 'deletion'] as TypeFilter[]).map(f => (
            <button key={f} onClick={() => setTypeFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all ${typeFilter === f
                ? f === 'deletion' ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : f === 'cancellation' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border border-[var(--border-primary)] hover:bg-[var(--bg-surface)]'
                }`}>
              {f === 'all' ? 'Alle' : f === 'cancellation' ? 'Oppsigelse' : 'Sletting'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mr-1">Periode:</span>
          {(['all', '30', '90', '365'] as PeriodFilter[]).map(f => (
            <button key={f} onClick={() => setPeriodFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all ${periodFilter === f
                ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border border-[var(--border-primary)] hover:bg-[var(--bg-surface)]'
                }`}>
              {f === 'all' ? 'Alt' : f === '30' ? '30 d' : f === '90' ? '90 d' : '12 mnd'}
            </button>
          ))}
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <MessageSquareHeart size={16} className="text-emerald-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Svar</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{counts.total}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Totalt i utvalget</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-amber-500/10 p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <UserX size={16} className="text-amber-400" />
            </div>
            <p className="text-2xs font-black text-amber-400 uppercase tracking-widest">Oppsigelser</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{counts.cancellation}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Sa opp abonnement</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-red-500/10 p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Trash2 size={16} className="text-red-400" />
            </div>
            <p className="text-2xs font-black text-red-400 uppercase tracking-widest">Slettinger</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{counts.deletion}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Slettet konto</p>
        </div>
        <div className="bg-[var(--bg-surface-alt)] border border-sky-500/10 p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-sky-500/10 flex items-center justify-center">
              <MessageSquare size={16} className="text-sky-400" />
            </div>
            <p className="text-2xs font-black text-sky-400 uppercase tracking-widest">Kommentarer</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{counts.withComment}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Med fritekst</p>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--border-primary)] rounded-2xl py-16 flex flex-col items-center gap-2">
          <MessageSquareHeart size={24} className="text-[var(--text-muted)]" />
          <p className="text-tiny text-[var(--text-muted)] font-black uppercase tracking-widest">Ingen tilbakemeldinger i utvalget</p>
        </div>
      ) : (
        <>
          {/* Distribusjoner */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-7">
              <p className="text-sm font-black text-[var(--text-primary)] mb-1">Hvorfor de forlater oss</p>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest mb-5">
                {reasonTally.answered} av {counts.total} svarte
              </p>
              <BarList rows={reasonTally.rows} answered={reasonTally.answered} label={reasonLabel} tone="bg-amber-500" />
            </div>
            <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-7">
              <p className="text-sm font-black text-[var(--text-primary)] mb-1">Hva skulle til for å beholde dem</p>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest mb-5">
                {returnTally.answered} av {counts.total} svarte
              </p>
              <BarList rows={returnTally.rows} answered={returnTally.answered} label={returnLabel} tone="bg-emerald-500" />
            </div>
          </div>

          {/* Segmentering */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-7">
            <p className="text-sm font-black text-[var(--text-primary)] mb-5">Segmentering</p>
            <div className="flex gap-8 flex-wrap">
              <SegmentRow title="Leverandør" rows={providerSeg} render={providerLabel} />
              <SegmentRow title="Plattform" rows={platformSeg} />
              <SegmentRow title="Språk" rows={langSeg} />
            </div>
          </div>

          {/* Kommentarer */}
          <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-7">
            <div className="flex items-center gap-3 mb-5">
              <MessageSquare size={18} className="text-sky-400" />
              <p className="text-sm font-black text-[var(--text-primary)]">Fritekst-kommentarer ({comments.length})</p>
            </div>
            {comments.length === 0 ? (
              <p className="text-tiny text-[var(--text-muted)]">Ingen kommentarer i utvalget.</p>
            ) : (
              <div className="space-y-3">
                {comments.map(s => (
                  <div key={s.id} className="bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-2xl p-4">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${
                        s.type === 'deletion'
                          ? 'bg-red-500/10 text-red-400 border-red-500/20'
                          : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}>
                        {s.type === 'deletion' ? 'Sletting' : 'Oppsigelse'}
                      </span>
                      {s.reason && (
                        <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border border-[var(--border-secondary)]">
                          {reasonLabel(s.reason)}
                        </span>
                      )}
                      {s.subscriptionProvider && (
                        <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border border-[var(--border-secondary)]">
                          {providerLabel(s.subscriptionProvider)}
                        </span>
                      )}
                      {s.source && s.source.endsWith('_webhook') && (
                        <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-sky-500/10 text-sky-400 border border-sky-500/20">
                          Auto-detektert
                        </span>
                      )}
                      <span className="text-3xs text-[var(--text-muted)] ml-auto">{fmtDateTime(s.createdAt)}</span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] italic leading-relaxed">"{s.comment}"</p>
                    {(s.userName || s.userEmail) && (
                      <p className="text-3xs text-[var(--text-muted)] mt-2">{s.userName || ''}{s.userEmail ? ` · ${s.userEmail}` : ''}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminOffboardingDashboard;
